import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'

import {
  NetworkService,
  OfflineGameService,
  OfflineLeadService,
  OfflineSpinService,
} from 'offline'

const AUTO_COLLAPSE_DELAY = 5000

const OfflineStatusIndicator = () => {
  const [apiOnline, setApiOnline] =
    useState(false)

  const [pendingLeads, setPendingLeads] =
    useState(0)

  const [pendingSpins, setPendingSpins] =
    useState(0)

  const [syncing, setSyncing] =
    useState(false)

  const [
    lastSyncAt,
    setLastSyncAt,
  ] = useState(null)

  /*
   * Começa aberto.
   */
  const [
    expanded,
    setExpanded,
  ] = useState(true)

  const collapseTimerRef =
    useRef(null)

  const previousOnlineRef =
    useRef(null)

  /*
   * =========================================================
   * TIMER DE RECOLHIMENTO
   * =========================================================
   */

  const clearCollapseTimer =
    useCallback(() => {
      if (
        collapseTimerRef.current
      ) {
        window.clearTimeout(
          collapseTimerRef.current,
        )

        collapseTimerRef.current =
          null
      }
    }, [])

  const scheduleCollapse =
    useCallback(() => {
      clearCollapseTimer()

      collapseTimerRef.current =
        window.setTimeout(
          () => {
            setExpanded(false)

            collapseTimerRef.current =
              null
          },
          AUTO_COLLAPSE_DELAY,
        )
    }, [
      clearCollapseTimer,
    ])

  const showTemporarily =
    useCallback(() => {
      setExpanded(true)
      scheduleCollapse()
    }, [
      scheduleCollapse,
    ])

  /*
   * =========================================================
   * CONSULTA STATUS
   * =========================================================
   */

  const refreshStatus =
    useCallback(
      async () => {
        /*
         * API REAL
         */
        try {
          const online =
            await NetworkService
              .checkApiConnection()

          /*
           * Se mudou ONLINE <-> OFFLINE,
           * mostramos o indicador novamente.
           */
          if (
            previousOnlineRef.current !==
              null &&
            previousOnlineRef.current !==
              online
          ) {
            showTemporarily()
          }

          previousOnlineRef.current =
            online

          setApiOnline(
            online,
          )
        } catch (error) {
          if (
            previousOnlineRef.current ===
            true
          ) {
            showTemporarily()
          }

          previousOnlineRef.current =
            false

          setApiOnline(false)
        }

        /*
         * LEADS PENDENTES
         */
        try {
          const leads =
            await OfflineLeadService
              .getPendingLeads()

          setPendingLeads(
            Array.isArray(leads)
              ? leads.length
              : 0,
          )
        } catch (error) {
          setPendingLeads(0)
        }

        /*
         * GIROS PENDENTES
         */
        try {
          const spins =
            await OfflineSpinService
              .getPendingSpins()

          setPendingSpins(
            Array.isArray(spins)
              ? spins.length
              : 0,
          )
        } catch (error) {
          setPendingSpins(0)
        }

        /*
         * ÚLTIMA SINCRONIZAÇÃO
         */
        try {
          const value =
            await OfflineGameService
              .getMeta(
                'last-successful-sync',
              )

          setLastSyncAt(
            value || null,
          )
        } catch (error) {
          setLastSyncAt(null)
        }
      },
      [
        showTemporarily,
      ],
    )

  /*
   * =========================================================
   * EVENTOS
   * =========================================================
   */

  useEffect(() => {
    /*
     * Primeira exibição.
     */
    refreshStatus()

    scheduleCollapse()

    /*
     * Atualização periódica.
     */
    const timer =
      window.setInterval(
        () => {
          refreshStatus()
        },
        10000,
      )

    /*
     * Navegador detectou mudança
     * de conectividade.
     */
    const handleConnectionChange =
      () => {
        showTemporarily()

        refreshStatus()
      }

    /*
     * Começou sincronização.
     */
    const handleSyncStarted =
      () => {
        setSyncing(true)

        clearCollapseTimer()

        setExpanded(true)
      }

    /*
     * Terminou sincronização.
     */
    const handleSyncFinished =
      async (event) => {
        setSyncing(false)

        const result =
          event?.detail

        if (
          result &&
          result.skipped !== true
        ) {
          const now =
            new Date()
              .toISOString()

          try {
            await OfflineGameService
              .saveMeta(
                'last-successful-sync',
                now,
              )

            setLastSyncAt(
              now,
            )
          } catch (error) {
            console.warn(
              '[OFFLINE STATUS] Não foi possível salvar data da sincronização.',
              error,
            )
          }
        }

        showTemporarily()

        refreshStatus()
      }

    window.addEventListener(
      'online',
      handleConnectionChange,
    )

    window.addEventListener(
      'offline',
      handleConnectionChange,
    )

    window.addEventListener(
      'games:offline-sync-started',
      handleSyncStarted,
    )

    window.addEventListener(
      'games:offline-sync-finished',
      handleSyncFinished,
    )

    return () => {
      window.clearInterval(
        timer,
      )

      clearCollapseTimer()

      window.removeEventListener(
        'online',
        handleConnectionChange,
      )

      window.removeEventListener(
        'offline',
        handleConnectionChange,
      )

      window.removeEventListener(
        'games:offline-sync-started',
        handleSyncStarted,
      )

      window.removeEventListener(
        'games:offline-sync-finished',
        handleSyncFinished,
      )
    }
  }, [
    clearCollapseTimer,
    refreshStatus,
    scheduleCollapse,
    showTemporarily,
  ])

  /*
   * =========================================================
   * ABRIR MANUALMENTE
   * =========================================================
   */

  const handleClick =
    () => {
      /*
       * Se estiver recolhido:
       * abre e agenda novo recolhimento.
       */
      if (!expanded) {
        showTemporarily()

        return
      }

      /*
       * Se estiver aberto:
       * permite recolher imediatamente.
       */
      clearCollapseTimer()

      setExpanded(false)
    }

  const pendingTotal =
    pendingLeads +
    pendingSpins

  /*
   * =========================================================
   * CORES
   * =========================================================
   */

  const background =
    syncing
      ? 'rgba(245, 158, 11, .96)'
      : apiOnline
        ? 'rgba(22, 163, 74, .96)'
        : 'rgba(220, 38, 38, .96)'

  const label =
    syncing
      ? 'SINCRONIZANDO'
      : apiOnline
        ? 'ONLINE'
        : 'OFFLINE'

  const formatLastSync =
    () => {
      if (!lastSyncAt) {
        return 'Nunca'
      }

      try {
        return new Date(
          lastSyncAt,
        ).toLocaleTimeString(
          'pt-BR',
          {
            hour:
              '2-digit',

            minute:
              '2-digit',

            second:
              '2-digit',
          },
        )
      } catch (error) {
        return '—'
      }
    }

  /*
   * =========================================================
   * RECOLHIDO
   * =========================================================
   */

  if (!expanded) {
    return (
      <button
        type="button"

        title={`Status: ${label}`}

        aria-label={`Status da conexão: ${label}. Toque para abrir.`}

        onClick={
          handleClick
        }

        style={{
          position:
            'fixed',

          right:
            0,

          bottom:
            18,

          zIndex:
            9999999,

          width:
            44,

          height:
            58,

          padding:
            0,

          border:
            'none',

          borderRadius:
            '16px 0 0 16px',

          background,

          boxShadow:
            '0 6px 22px rgba(0,0,0,.30)',

          display:
            'flex',

          alignItems:
            'center',

          justifyContent:
            'center',

          cursor:
            'pointer',

          transition:
            'all .25s ease',

          WebkitTapHighlightColor:
            'transparent',
        }}
      >
        <span
          style={{
            width:
              13,

            height:
              13,

            borderRadius:
              '50%',

            background:
              '#ffffff',

            boxShadow:
              '0 0 0 4px rgba(255,255,255,.18)',
          }}
        />

        {pendingTotal > 0 && (
          <span
            style={{
              position:
                'absolute',

              top:
                5,

              left:
                4,

              minWidth:
                18,

              height:
                18,

              padding:
                '0 4px',

              borderRadius:
                9,

              background:
                '#ffffff',

              color:
                apiOnline
                  ? '#15803d'
                  : '#b91c1c',

              display:
                'flex',

              alignItems:
                'center',

              justifyContent:
                'center',

              fontSize:
                10,

              fontWeight:
                900,
            }}
          >
            {pendingTotal}
          </span>
        )}
      </button>
    )
  }

  /*
   * =========================================================
   * ABERTO
   * =========================================================
   */

  return (
    <button
      type="button"

      aria-label="Status de conexão. Toque para recolher."

      onClick={
        handleClick
      }

      style={{
        position:
          'fixed',

        right:
          16,

        bottom:
          18,

        zIndex:
          9999999,

        minWidth:
          235,

        maxWidth:
          310,

        padding:
          '12px 17px',

        border:
          'none',

        borderRadius:
          18,

        background,

        color:
          '#ffffff',

        boxShadow:
          '0 8px 28px rgba(0,0,0,.30)',

        cursor:
          'pointer',

        textAlign:
          'left',

        userSelect:
          'none',

        WebkitUserSelect:
          'none',

        WebkitTapHighlightColor:
          'transparent',

        transition:
          'all .25s ease',
      }}
    >
      <div
        style={{
          display:
            'flex',

          alignItems:
            'center',

          justifyContent:
            'center',

          gap:
            10,

          fontSize:
            17,

          fontWeight:
            900,
        }}
      >
        <span
          style={{
            width:
              11,

            height:
              11,

            flex:
              '0 0 11px',

            borderRadius:
              '50%',

            background:
              '#ffffff',
          }}
        />

        <span>
          {label}
        </span>
      </div>

      {pendingTotal > 0 && (
        <div
          style={{
            marginTop:
              8,

            paddingTop:
              8,

            borderTop:
              '1px solid rgba(255,255,255,.22)',

            display:
              'flex',

            justifyContent:
              'center',

            gap:
              14,

            fontSize:
              11,

            fontWeight:
              700,
          }}
        >
          <span>
            Leads: {pendingLeads}
          </span>

          <span>
            Giros: {pendingSpins}
          </span>
        </div>
      )}

      <div
        style={{
          marginTop:
            pendingTotal > 0
              ? 7
              : 8,

          textAlign:
            'center',

          opacity:
            0.88,

          fontSize:
            11,

          fontWeight:
            700,
        }}
      >
        Última sincronização:{' '}
        {formatLastSync()}
      </div>
    </button>
  )
}

export default OfflineStatusIndicator